import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Minus, Trash2, ShoppingBag, ShieldCheck, Star } from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney } from '@/lib/realtime';
import { usePolicy } from '@/lib/policy';
import { KioskLockBar } from '@/lib/kioskLock';
import FeedbackPrompt from '@/components/FeedbackPrompt';

/**
 * Table Mode for in-restaurant iPads. Locked to a table_id (URL param).
 * No login required; uses table_id from path.
 */
export default function TableMode() {
  const { tableId } = useParams();
  const [sp] = useSearchParams();
  const qrToken = sp.get('qr');
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [table, setTable] = useState(null);
  const [branch, setBranch] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/menu/items'),
      api.get('/menu/categories'),
    ]).then(([i, c]) => { setItems(i.data || []); setCategories(c.data || []); });
    if (qrToken) {
      api.get(`/branches/tables/by-qr/${qrToken}`).then((r) => { setTable(r.data.table); setBranch(r.data.branch); }).catch(() => {});
    } else if (tableId) {
      api.get('/branches').then(async (br) => {
        const branches = br.data || [];
        for (const b of branches) {
          const tr = await api.get(`/branches/${b.id}/tables`);
          const t = (tr.data || []).find((x) => x.id === tableId);
          if (t) { setTable(t); setBranch(b); break; }
        }
      });
    }
  }, [tableId, qrToken]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((it) =>
      (activeCat === 'all' || it.category_id === activeCat) &&
      (!s || it.name.toLowerCase().includes(s) || (it.description || '').toLowerCase().includes(s)),
    );
  }, [items, activeCat, search]);

  const add = (m) => setCart((p) => {
    const existing = p.find((x) => x.menu_item_id === m.id);
    if (existing) return p.map((x) => x.menu_item_id === m.id ? { ...x, quantity: x.quantity + 1 } : x);
    return [...p, { menu_item_id: m.id, name: m.name, price: m.price, image_url: m.image_url, quantity: 1 }];
  });
  const inc = (id, by) => setCart((p) => p.map((x) => x.menu_item_id === id ? { ...x, quantity: Math.max(0, x.quantity + by) } : x).filter((x) => x.quantity > 0));
  const remove = (id) => setCart((p) => p.filter((x) => x.menu_item_id !== id));

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const { policy } = usePolicy();
  const taxRate = policy.tax_enabled ? Number(policy.tax_rate || 0) : 0;
  const tax = +(subtotal * taxRate).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);

  const send = async () => {
    if (!cart.length) return;
    setSubmitting(true);
    try {
      const payload = {
        branch_id: branch?.id,
        order_type: 'dine_in',
        items: cart.map((c) => ({ menu_item_id: c.menu_item_id, quantity: c.quantity })),
        payment_method: 'cash',
        customer_name: `Table ${table?.table_number || ''}`,
      };
      if (qrToken) payload.table_qr_token = qrToken;
      else if (tableId) payload.table_id = tableId;
      const { data: order } = await api.post('/orders', payload);
      setLastOrder(order);
      setCart([]);
      toast.success(`Order sent to kitchen · #${order.order_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <KioskLockBar title="Table" position="bottom-right" />
      <header data-testid="table-mode-banner" className="sticky top-0 z-20 border-b border-border bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div>
            <p className="text-xs uppercase tracking-wider opacity-80">Dine-in mode</p>
            <h1 className="font-display text-2xl font-semibold">Table {table?.table_number || tableId} · {branch?.name || 'City Grill'}</h1>
          </div>
          <div className="flex items-center gap-2 text-xs opacity-80">
            <ShieldCheck className="h-4 w-4" /> Staff PIN required to exit
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[220px_1fr_400px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 flex flex-col gap-1">
            <Button data-testid="table-cat-all" variant={activeCat === 'all' ? 'default' : 'ghost'} className="justify-start" onClick={() => setActiveCat('all')}>All</Button>
            {categories.map((c) => (
              <Button key={c.id} data-testid="table-cat" variant={activeCat === c.id ? 'default' : 'ghost'} className="justify-start text-left" onClick={() => setActiveCat(c.id)}>{c.name}</Button>
            ))}
          </div>
        </aside>

        <div>
          <Input data-testid="table-search-input" placeholder="Search menu…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-4 h-12 text-base" />
          <div className="grid gap-3 sm:grid-cols-2">
            {filtered.map((it) => (
              <Card key={it.id} data-testid="table-menu-item" className="overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden"><img src={it.image_url} alt={it.name} className="h-full w-full object-cover" /></div>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold">{it.name}</h3>
                      <p className="line-clamp-2 text-sm text-muted-foreground">{it.description}</p>
                    </div>
                    <span className="tabular text-base font-semibold">{fmtMoney(it.price)}</span>
                  </div>
                  <Button data-testid="table-add-button" className="mt-3 h-12 w-full" onClick={() => add(it)}>Add to order</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <aside>
          <Card className="sticky top-24">
            <CardHeader><CardTitle>Your order</CardTitle></CardHeader>
            <CardContent>
              {lastOrder && (
                <div className="mb-3 rounded-md bg-emerald-50 p-2 text-sm text-emerald-900">
                  Order #{lastOrder.order_number} sent to kitchen. Bill on demand.
                  <Button data-testid="table-rate-visit-button" size="sm" variant="ghost" className="mt-1 w-full text-emerald-900 hover:bg-emerald-100" onClick={() => setFeedbackOpen(true)}>
                    <Star className="mr-1 h-4 w-4" /> Rate your visit
                  </Button>
                </div>
              )}
              {!cart.length && <p className="text-sm text-muted-foreground">Tap items to add</p>}
              <ul className="space-y-2">
                {cart.map((i) => (
                  <li key={i.menu_item_id} className="flex items-center gap-2">
                    <img src={i.image_url} alt={i.name} className="h-10 w-10 rounded object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{i.name}</p>
                      <p className="text-xs text-muted-foreground tabular">{fmtMoney(i.price)} each</p>
                    </div>
                    <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => inc(i.menu_item_id, -1)}><Minus className="h-3 w-3" /></Button>
                    <span className="w-5 text-center text-sm tabular">{i.quantity}</span>
                    <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => inc(i.menu_item_id, 1)}><Plus className="h-3 w-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(i.menu_item_id)}><Trash2 className="h-3 w-3" /></Button>
                  </li>
                ))}
              </ul>
              {!!cart.length && (
                <div className="mt-3 space-y-1 border-t border-border pt-3 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular">{fmtMoney(subtotal)}</span></div>
                  {policy.tax_enabled && taxRate > 0 && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(2).replace(/\.00$/, '')}%)</span><span className="tabular">{fmtMoney(tax)}</span></div>
                  )}
                  <div className="flex justify-between text-base font-semibold"><span>Total</span><span className="tabular">{fmtMoney(total)}</span></div>
                </div>
              )}
              <Button data-testid="table-mode-send-order-button" className="mt-4 h-14 w-full text-base" disabled={!cart.length || submitting} onClick={send}><ShoppingBag className="mr-2 h-5 w-5" /> Send to kitchen</Button>
            </CardContent>
          </Card>
        </aside>
      </div>
      <FeedbackPrompt
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        orderId={lastOrder?.id}
        source="table"
        authed={false}
        title="How was your visit?"
        description={`Table ${table?.table_number || ''} · ${branch?.name || 'City Grill'}`}
      />
    </div>
  );
}
